<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import Banner from '@/components/ComplexBanner.vue'
import { integrationsModule } from '@/store/integrations/module'
import { toastModule } from '@/store/toast/module'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { instructorModule } from '@/store/instructor/module'
import { organizationModule } from '@/store/organization/module'
import { analyticsModule } from '@/store/analytics/module'
import type { Teach } from '@pocketprep/types'
import { bundleModule } from '@/store/bundle/module'

type SelectEntry = {
    label: string
    value: string
}

type OrgGroupIdsCollection = {
    existingOrgGroupIds: string[]
    newOrgGroupPromises: Promise<Teach.Class.OrgGroupJSON>[]
}

const {
    SidePanel,
    Button: PocketButton,
    Select: PocketSelect,
    Input: PocketInput,
    Errors,
    Icon,
    Tooltip,
} = UIKit

const props = withDefaults(
    defineProps<{
        clientId?: string
        lmsId?: string
    }>(),
    { clientId: '', lmsId: '' }
)

const emit = defineEmits<{
    close: [value?: boolean]
}>()

const deploymentId = ref('')
const courseName = ref('')
const deploymentIdRegex = /^\d+:[a-zA-Z0-9]+$/
const isLoading = ref(false)
const errors = ref<string[]>([])

const showRemoveInstructorTooltip = ref<null | string>(null)
const showAddInstructorTooltip = ref<null | string>(null)
const instructorOptions = ref<{ value: string; label: string }[]>([
    { value: '', label: '' },
])

// eslint-disable-next-line max-len
const viewInstructionsLink = 'https://help.educators.pocketprep.com/en/articles/9953690-adding-pocket-prep-to-a-canvas-course'

const exam = ref<null | SelectEntry>(null)
const group = ref<null | SelectEntry>(null)

const instructorSelectedIds = ref<string[]>([])

const setRemoveInstructorTooltip = (id: string | null) => {
    showRemoveInstructorTooltip.value = id
}

const clearRemoveInstructorTooltip = () => {
    showRemoveInstructorTooltip.value = null
}

const setAddInstructorTooltip = (id: string | null) => {
    showAddInstructorTooltip.value = id
}

const clearAddInstructorTooltip = () => {
    showAddInstructorTooltip.value = null
}

const isValidDeploymentId = computed(() => {
    return deploymentIdRegex.test(deploymentId.value)
})

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const mappedExams = computed(() => {
    const activeCurrentOrgExams = Object.values(currentOrgExams.value)
        .filter(em => {
            const currEm = examMetadata.value[em.examMetadata.objectId]
            return currEm !== undefined && !examModule.getters.getIsExamMetadataRemoved(currEm)
        })

    const counts = activeCurrentOrgExams.reduce((acc, ci) => {
        acc[ci.displayName] = (acc[ci.displayName] || 0) + 1
        return acc
    }, {} as {
        [orgExamDisplayName: string]: number
    })

    return activeCurrentOrgExams.map(ci => {
        const currExamMetadata = examMetadata.value[ci.examMetadata.objectId]
        const count = counts[ci.displayName] || 0
        const label = count > 1 && currExamMetadata ? currExamMetadata.releaseInfo.name : ci.displayName
        return { label, value: ci.objectId }
    })
})

const mappedGroups = computed(() => {
    return Object.values(currentOrgGroups.value).reduce((acc, cog) => {
        if (cog.orgExam.objectId === exam.value?.value && cog.type !== 'system') {
            acc.push({ label: cog.name, value: cog.objectId })
        }
        return acc
    }, [] as SelectEntry[])
})

const mappedInstructors = computed(() => {
    return Object.values(currentOrgInstructors.value || []).reduce((acc, coi) => {
        const instructorUser = currentOrgInstructorUsers.value?.[coi.user.objectId]
        const firstString = instructorUser?.firstName || instructorUser?.email
        const lastName = instructorUser?.lastName
        const instructorString = lastName
            ? `${firstString} ${lastName}`
            : `${firstString}`

        if (!instructorSelectedIds.value.includes(coi.objectId)) {
            acc.push({
                label: instructorString,
                value: coi.objectId,
            })
        }
        return acc
    }, [] as SelectEntry[])
})

const getOrgGroupForInstructorAndExam = (instructorId: string, orgExamId: string) => {
    return Object.values(currentOrgGroups.value).find(g => {
        return g.type === 'system'
            && g.owner
            && g.owner.objectId === instructorId
            && g.orgExam.objectId === orgExamId
    })
}

const emitClose = () => {
    emit('close', true)
}

const addIntegration = async () => {
    isLoading.value = true
    const orgExamId = exam.value?.value
    if (!exam.value || !orgExamId) {
        throw new Error('Unable to add an integration if no exam is selected!')
    }
    const instructorOrgGroupIds = await createOrgGroupsForInstructors()

    const examOrgGroupId = (groupModule.getters.searchCurrentOrgGroups(
        { examId: orgExamId, type: 'system' }
    ).find(eog => !eog.owner))?.objectId

    const groupId = group.value?.value

    const orgGroupIds = [ ...instructorOrgGroupIds, examOrgGroupId, groupId ].filter(id => id !== undefined)

    await integrationsModule.actions.upsertLMSCourseIntegration({
        deploymentId: deploymentId.value,
        lmsId: props.lmsId,
        orgGroupIds,
        courseName: courseName.value,
    })

    if (currentOrganization.value) {
        analyticsModule.actions.amplitudeTrack('Course Integration Created', {
            examName: exam.value.label,
            courseName: courseName.value,
            organizationId: currentOrganization.value.objectId,
            organizationName: currentOrganization.value.name,
        })
    }

    toastModule.actions.displayToast({
        title: 'Canvas course integrated.',
    })

    emitClose()
}

const createOrgGroupsForInstructors = async (): Promise<string[]> => {
    const selectedOrgInstructorIds = instructorOptions.value.map(io => io.value)

    const orgExamId = exam.value?.value
    const organizationId = currentOrganization.value?.objectId

    if (!orgExamId || !organizationId) {
        return []
    }
    const orgGroupCollection = selectedOrgInstructorIds.reduce<OrgGroupIdsCollection>((acc, soii) => {
        const orgGroupForExam = getOrgGroupForInstructorAndExam(soii, orgExamId)

        if (orgGroupForExam) {
            acc.existingOrgGroupIds.push(orgGroupForExam.objectId)
            return acc
        }
        const instructor = currentOrgInstructors.value?.[soii]

        if (!instructor) {
            return acc
        }

        const instructorUser = currentOrgInstructorUsers.value?.[instructor.user.objectId]

        acc.newOrgGroupPromises.push(groupModule.actions.addGroup({
            organizationId,
            name: `${instructorUser?.lastName} - ${exam.value?.label}`,
            type: 'system',
            orgExamId,
            orgStudentIds: [],
            isPubliclyEditable: false,
            ownerId: instructor.objectId,
        }))
        return acc
    }, { existingOrgGroupIds: [], newOrgGroupPromises: [] })

    const newOrgGroupIds = (await Promise.all(orgGroupCollection.newOrgGroupPromises))
        .map(nog => nog.objectId)
    return [ ...orgGroupCollection.existingOrgGroupIds, ...newOrgGroupIds ]
}

const addInstructor = (instructorId?: string) => {
    if (!instructorId) {
        return
    }
    showAddInstructorTooltip.value = null
    instructorSelectedIds.value.push(instructorId)
    instructorOptions.value.push({ value: '', label: '' })
}

const removeInstructor = (instructorId: string) => {
    showRemoveInstructorTooltip.value = null
    const instructorIdIndex = instructorSelectedIds.value.indexOf(instructorId)

    instructorSelectedIds.value.splice(instructorIdIndex, 1)

    const instructorOptionIndex = instructorOptions.value.findIndex(io => io.value === instructorId)
    instructorOptions.value.splice(instructorOptionIndex, 1)

    if (!instructorOptions.value.length) {
        instructorOptions.value.push({ value: '', label: '' })
    }
}

const checkDisplayErrorBanner = () => {
    if (deploymentId.value && !isValidDeploymentId.value) {
        errors.value.push('The Deployment ID you entered is not valid.')
    } else {
        errors.value = []
    }
}

onMounted(async () => {
    await Promise.all([
        bundleModule.actions.fetchBundles(),
        examModule.actions.fetchCurrentOrgExams(),
        examModule.actions.fetchExamMetadata(),
        integrationsModule.actions.fetchLMSIntegrations(),
        groupModule.actions.fetchCurrentOrgGroups(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
    ])
})
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            class="add-integration"
            name="Add Integration to Course"
            :isTeachSidePanel="true"
            @close="emitClose"
        >
            <Banner
                class="add-integration__banner"
                title="Your Canvas courses's Deployment ID is needed for this integration."
                :link-url="viewInstructionsLink"
            >
                We have a guide to walk you through enabling SSO on Canvas.
                Once you have SSO enabled, students and instructors will both be able to automatically
                log in to Pocket Prep via a link in the Canvas course navigation.
            </Banner>
            <div class="add-integration__client-id-container">
                <span class="add-integration__client-id-label">
                    Client ID:
                </span>
                {{ props.clientId }}
            </div>
            <PocketInput
                v-model="deploymentId"
                class="add-integration__deployment-id"
                field-type="text"
                label="Deployment ID"
                :error="!!errors.length"
                @focusout="checkDisplayErrorBanner"
            />
            <Errors
                v-if="errors.length"
                class="add-integration__error-banner"
                :errors="errors"
            />
            <PocketInput
                v-model="courseName"
                class="add-integration__course-name"
                field-type="text"
                label="Course Name"
                :error="!!errors.length"
            />
            <div class="add-integration__course-details-container">
                <label class="add-integration__course-details">
                    Course Details
                </label>
                <p class="add-integration__course-details-subtext">
                    These details will automatically be associated with any students who click the SSO link on Canvas.
                </p>
            </div>
            <div class="add-integration__selects">
                <PocketSelect
                    v-model="exam"
                    class="add-integration__select-exam"
                    label="Exam"
                    placeholder="Select Exam"
                    :showEmptyOption="true"
                    :data="mappedExams"
                />
                <PocketSelect
                    v-model="group"
                    :typeahead="!!exam"
                    class="add-integration__select-group"
                    label="Group (optional)"
                    :disabled="!exam"
                    :data="mappedGroups"
                />
                <div
                    v-if="exam && mappedInstructors"
                    class="add-integration__instructors"
                >
                    <div class="add-integration__instructors-label">
                        Instructors (optional)
                    </div>
                    <div
                        v-for="(option, index) in instructorOptions"
                        :key="option.value"
                        class="add-integration__instructor-row"
                    >
                        <PocketSelect
                            v-model="instructorOptions[index]"
                            class="add-integration__select-instructor"
                            :data="mappedInstructors"
                        />
                        <div
                            class="add-integration__instructor-row-remove"
                            @click="instructorOptions[index]?.value && removeInstructor(instructorOptions[index].value)"
                            @mouseover="setRemoveInstructorTooltip(`${index}_${instructorOptions[index]?.value}`)"
                            @mouseout="clearRemoveInstructorTooltip"
                        >
                            <Tooltip
                                v-if="showRemoveInstructorTooltip === `${index}_${instructorOptions[index]?.value}`"
                                theme="leftalign"
                                :disabled="instructorOptions[index]?.value === ''"
                            >
                                Remove Instructor
                            </Tooltip>
                            <Icon type="close" />
                        </div>
                        <div
                            v-if="index === instructorOptions.length - 1"
                            class="add-integration__instructor-row-add"
                            :class="{
                                'add-integration__instructor-row-add--not-enabled': !instructorOptions[index]?.value
                            }"
                            @click="addInstructor(instructorOptions[index]?.value)"
                            @mouseover="setAddInstructorTooltip(`${index}_${instructorOptions[index]?.value}`)"
                            @mouseout="clearAddInstructorTooltip"
                        >
                            <Tooltip
                                v-if="instructorOptions[index]?.value
                                    && showAddInstructorTooltip === `${index}_${instructorOptions[index].value}`"
                                theme="leftalign"
                            >
                                Add Instructor
                            </Tooltip>
                            <Icon type="addCircle" />
                        </div>
                    </div>
                </div>
            </div>
            <template #action>
                <PocketButton
                    class="add-integration__connect-btn"
                    :disabled="isLoading
                        || !deploymentId
                        || (!!errors.length && !!deploymentId)
                        || !isValidDeploymentId
                        || !exam"
                    @click="addIntegration"
                >
                    Add Integration
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.add-integration {
    :deep(*) {
        .uikit-side-panel__content {
            padding: 36px 32px;
        }
        .uikit-tooltip {
            position: absolute;
            top: -30px;
            left: 22px;
        }
    }

    &__course-name,
    &__deployment-id {
        margin-top: 24px;
    }

    &__error-banner {
        margin-top: 12px;
    }

    &__label {
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        margin: 0 0 4px 12px;
        color: $pickled-bluewood;
        display: block;
    }

    &__client-id-container {
        font-size: 13px;
        line-height: 20px;
        margin-top: 24px;
    }

    &__client-id-label {
        font-weight: 600;
    }

    &__course-details-container {
        margin-top: 60px;
    }

    &__course-details {
        font-weight: 600;
    }

    &__course-details-subtext {
        line-height: 20px;
        margin: 7px 8px 0 0;
    }

    &__select-exam,
    &__select-instructor,
    &__select-group {
        margin-top: 24px;
    }

    &__select-instructor {
        width: 433px;
        margin: 0;
    }

    &__instructors-label {
        margin: 27px 0 -19px 12px;
        font-size: 13px;
        font-weight: 600;
        color: $pickled-bluewood;
    }

    &__instructor-row {
        height: 36px;
        display: flex;
        align-items: center;
        margin: 23px 0 20px;
    }

    &__instructor-row-add,
    &__instructor-row-remove {
        position: relative;
        padding: 4px 0 0 12px;
        color: $brand-blue;

        &--not-enabled {
            color: $fog;
        }
    }
}
</style>
